'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { Button } from '@/components/ui/Button';
import { MessageSquare, Bot, Calendar, TrendingUp, Shield, Mic } from 'lucide-react';

export default function HomePage() {
  const router = useRouter();
  const [showChat, setShowChat] = useState(false);
  const [startWithVoice, setStartWithVoice] = useState(false);

  const openChat = (withVoice: boolean) => {
    setStartWithVoice(withVoice);
    setShowChat(true);
  };

  const features = [
    {
      icon: Bot,
      title: 'AI Conversazionale',
      description: 'Interagisci via chat o voce con il nostro assistente intelligente',
    },
    {
      icon: TrendingUp,
      title: 'Analisi Personalizzata',
      description: 'Identifichiamo le soluzioni AI piu adatte alle tue esigenze',
    },
    {
      icon: Calendar,
      title: 'Prenotazione Facile',
      description: 'Prenota una call con i nostri esperti in pochi click',
    },
    {
      icon: Shield,
      title: 'Privacy Garantita',
      description: 'I tuoi dati sono al sicuro e trattati con la massima riservatezza',
    },
  ];

  const handleBookingRequest = () => {
    router.push('/booking');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <header className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-800">
        <nav className="mx-auto flex max-w-7xl items-center justify-between p-6">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-8 w-8 text-white" />
            <span className="text-xl font-bold text-white">didatticloud</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => openChat(false)}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Chatta
            </Button>
            <Button
              variant="secondary"
              onClick={() => openChat(true)}
            >
              <Mic className="mr-2 h-4 w-4" />
              Parla
            </Button>
          </div>
        </nav>

        <div className="mx-auto max-w-7xl px-6 py-24 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-6xl">
            Scopri come l&apos;AI puo<br />
            trasformare la tua azienda
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-indigo-100">
            Parla con il nostro assistente AI per identificare le soluzioni di
            intelligenza artificiale piu adatte alle tue esigenze aziendali.
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Button
              size="lg"
              className="bg-white text-indigo-600 hover:bg-indigo-50"
              onClick={() => openChat(false)}
            >
              <MessageSquare className="mr-2 h-5 w-5" />
              Chatta con l&apos;assistente
            </Button>
            <Button
              size="lg"
              className="bg-white text-indigo-600 hover:bg-indigo-50"
              onClick={() => openChat(true)}
            >
              <Mic className="mr-2 h-5 w-5" />
              Parla con l&apos;assistente
            </Button>
            <Button
              size="lg"
              className="border-2 border-white bg-transparent text-white hover:bg-white/10"
              onClick={() => router.push('/booking')}
            >
              <Calendar className="mr-2 h-5 w-5" />
              Prenota una call
            </Button>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Come funziona
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Un processo semplice per trovare la soluzione AI perfetta per te
            </p>
          </div>

          <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="rounded-xl bg-white p-6 shadow-sm border border-gray-100"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100">
                  <feature.icon className="h-6 w-6 text-indigo-600" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  {feature.title}
                </h3>
                <p className="mt-2 text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-gray-100 py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Il tuo percorso verso l&apos;AI
            </h2>
          </div>

          <div className="mt-16 grid gap-8 lg:grid-cols-3">
            <div className="relative rounded-xl bg-white p-8 shadow-sm">
              <div className="absolute -top-4 left-8 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white font-bold">
                1
              </div>
              <h3 className="mt-4 text-xl font-semibold">Raccontaci di te</h3>
              <p className="mt-2 text-gray-600">
                Parla con il nostro assistente AI e descrivi la tua azienda,
                le sfide quotidiane e gli obiettivi che vuoi raggiungere.
              </p>
            </div>

            <div className="relative rounded-xl bg-white p-8 shadow-sm">
              <div className="absolute -top-4 left-8 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white font-bold">
                2
              </div>
              <h3 className="mt-4 text-xl font-semibold">Ricevi suggerimenti</h3>
              <p className="mt-2 text-gray-600">
                In base alle tue esigenze, ti suggeriamo gli agenti AI piu
                adatti dal nostro catalogo con una spiegazione personalizzata.
              </p>
            </div>

            <div className="relative rounded-xl bg-white p-8 shadow-sm">
              <div className="absolute -top-4 left-8 flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white font-bold">
                3
              </div>
              <h3 className="mt-4 text-xl font-semibold">Approfondisci</h3>
              <p className="mt-2 text-gray-600">
                Prenota una call con i nostri esperti per discutere nel
                dettaglio le soluzioni e pianificare l&apos;implementazione.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <div className="mx-auto max-w-2xl rounded-2xl bg-indigo-600 px-8 py-16">
            <h2 className="text-3xl font-bold text-white">
              Pronto a iniziare?
            </h2>
            <p className="mt-4 text-lg text-indigo-100">
              Inizia subito una conversazione con il nostro assistente AI.
              E gratuito e senza impegno.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                size="lg"
                className="bg-white text-indigo-600 hover:bg-indigo-50"
                onClick={() => openChat(false)}
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Chatta
              </Button>
              <Button
                size="lg"
                className="bg-white text-indigo-600 hover:bg-indigo-50"
                onClick={() => openChat(true)}
              >
                <Mic className="mr-2 h-5 w-5" />
                Parla
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-white py-12">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-6 w-6 text-indigo-600" />
              <span className="font-semibold text-gray-900">didatticloud</span>
            </div>
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} didatticloud. Tutti i diritti riservati.
            </p>
          </div>
        </div>
      </footer>

      {/* Chat Widget */}
      {showChat && (
        <ChatWidget
          onBookingRequest={handleBookingRequest}
          defaultOpen={true}
          startWithVoice={startWithVoice}
        />
      )}
    </div>
  );
}
